import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDaikinStatus, setPower, setSetpoint, setFanSpeed } from '../api/daikin';

// Clamp a setpoint to the unit's advertised min/max/step (matches the backend).
export function clampSetpoint(u, v) {
    const step = u.setpoint_step || 0.5;
    let t = Math.round((parseFloat(v) || 0) / step) * step;
    if (u.setpoint_min != null) t = Math.max(u.setpoint_min, t);
    if (u.setpoint_max != null) t = Math.min(u.setpoint_max, t);
    return Math.round(t * 10) / 10;
}

// Re-read while the card is open so Onecta changes show up. The backend serves
// its shared snapshot until it is 5 minutes old, so polling costs at most one
// Daikin call per 5 minutes however many people have the portal open.
const POLL_MS = 5 * 60 * 1000;

// Daikin status plus manual override. `pending` holds the setpoint the user is
// dialling in per unit before they press Set.
export default function useDaikinControl() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [conflict, setConflict] = useState(null);
    const [busy, setBusy] = useState({});
    const [pending, setPending] = useState({});
    const statusRef = useRef(null);

    // `changedId` is the unit just commanded: its dial follows the new state.
    // Any other unit the user is still dialling in (pending differs from what
    // it showed) keeps their value.
    const applyStatus = useCallback((s, changedId = null) => {
        if (!s) return;
        const shown = {};
        (statusRef.current?.units || []).forEach((u) => { shown[u.device_id] = u.setpoint; });
        statusRef.current = s;
        setStatus(s);
        setConflict(s.conflict || null);
        setPending((prev) => {
            const next = {};
            (s.units || []).forEach((u) => {
                const id = u.device_id;
                const dialling = id !== changedId && prev[id] != null && prev[id] !== shown[id];
                if (dialling) next[id] = prev[id];
                else if (u.setpoint != null) next[id] = u.setpoint;
            });
            return next;
        });
    }, []);

    const load = useCallback(async (refreshNow) => {
        setLoading(true);
        setError(null);
        try {
            applyStatus(await fetchDaikinStatus(refreshNow));
        } catch (e) {
            setError(`Could not load Daikin status: ${e.message}`);
        }
        setLoading(false);
    }, [applyStatus]);

    const refresh = useCallback(() => load(true), [load]);

    useEffect(() => {
        load(false);
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') load(false);
        }, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    const runForUnit = async (deviceId, fn, failMsg) => {
        setBusy((b) => ({ ...b, [deviceId]: true }));
        setError(null);
        try {
            // applyStatus also picks up any conflict the backend returned.
            applyStatus(await fn(), deviceId);
        } catch (e) {
            setError(`${failMsg}: ${e.message}`);
        }
        setBusy((b) => ({ ...b, [deviceId]: false }));
    };

    // expected = the value the user was looking at, so the backend can detect
    // an out-of-band Onecta change and refuse instead of clobbering it.
    const switchPower = (u, value) =>
        runForUnit(u.device_id, () => setPower(u.device_id, value, u.power), `Could not turn ${value}`);

    const applySetpoint = (u) =>
        runForUnit(u.device_id, () => setSetpoint(u.device_id, parseFloat(pending[u.device_id]), u.setpoint),
            'Could not set temperature');

    const nudgeSetpoint = (u, delta) =>
        setPending((p) => ({ ...p, [u.device_id]: clampSetpoint(u, (p[u.device_id] ?? u.setpoint) + delta) }));

    const applyFanSpeed = (u, step) =>
        runForUnit(u.device_id, () => setFanSpeed(u.device_id, step, u.fan_speed), 'Could not set fan speed');

    return {
        status, loading, error, conflict, busy, pending,
        refresh, switchPower, applySetpoint, nudgeSetpoint, applyFanSpeed,
    };
}
